import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { exportProductionSummary } from './exportProductionSummary';

const PRODUCTION_ORDERS_COLLECTION = 'production-orders';

/**
 * Exports a consolidated summary of all pending production orders
 * @param {Object} params
 * @param {Array} params.products - Products data
 * @param {Array} params.allRawMaterials - All raw materials data
 * @param {Array} params.allCategories - All categories data
 * @param {Function} params.getSimulation - Simulation function
 * @param {Function} params.showNotification - Notification callback
 */
export const exportPendingOpsSummary = async ({
    products,
    allRawMaterials,
    allCategories,
    getSimulation,
    showNotification
}) => {
    try {
        const q = query(
            collection(db, PRODUCTION_ORDERS_COLLECTION),
            where('status', '==', 'aguardando-mp')
        );
        const snap = await getDocs(q);
        const pendingOps = snap.docs.map(d => ({ id: d.id, ...d.data() }));

        if (pendingOps.length === 0) {
            showNotification("Nenhuma ordem pendente encontrada.", 'warning');
            return;
        }

        pendingOps.sort((a, b) => {
            const ta = a.createdAt?.toMillis?.() || 0;
            const tb = b.createdAt?.toMillis?.() || 0;
            return ta - tb;
        });

        // Consolidate all materials
        const allTotals = {};

        for (const op of pendingOps) {
            for (const item of op.items || []) {
                const productData = products.find(p => p.id === item.productId);

                if (!productData) {
                    console.warn(`Produto ${item.productId} não encontrado para OP ${op.opCode}`);
                    continue;
                }

                const simItem = {
                    ...productData,
                    id: item.productId,
                    requestedValue: item.quantity,
                    mode: item.mode,
                    gramatura: productData.gramatura,
                    gramaturaUnit: productData.gramaturaUnit,
                    lossIndex: productData.lossIndex || 1,
                    rawMaterials: productData.rawMaterials || [],
                };

                const sim = getSimulation(simItem);
                const allSimItems = [...sim.massItems, ...sim.unitItems];

                allSimItems.forEach(m => {
                    if (!m.id) return;

                    if (!allTotals[m.id]) {
                        allTotals[m.id] = {
                            id: m.id,
                            name: m.inciName || m.label,
                            needed: 0,
                            stock: m.stockActual,
                            unit: m.unit,
                            isWater: m.isWater
                        };
                    }
                    allTotals[m.id].needed += m.needed;
                });
            }
        }

        // Use the production summary exporter with consolidated data
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('OPs Pendentes - Consolidado');

        worksheet.columns = [
            { header: 'Cód. MP', key: 'code', width: 12 },
            { header: 'Nome Comercial', key: 'name', width: 80 },
            { header: 'INCI Name', key: 'inci', width: 80 },
            { header: 'Categoria', key: 'category', width: 25 },
            { header: 'Quantidade Necessária', key: 'needed', width: 35 },
            { header: 'Quantidade Faltante', key: 'missing', width: 20 },
            { header: 'Unid.', key: 'unit', width: 8 },
            { header: 'Valor Unitário', key: 'unitPrice', width: 15 },
            { header: 'Valor Total da Quantidade Total', key: 'totalPriceTotal', width: 35 },
            { header: 'Valor Total da Quantidade Faltante', key: 'totalPriceMissing', width: 35 },
            { header: 'Fornecedor', key: 'supplier', width: 80 },
            { header: 'Tipo', key: 'type', width: 20 },
        ];

        // Title
        const titleRow = worksheet.addRow({
            code: `CONSOLIDADO DE ${pendingOps.length} ORDENS DE PRODUÇÃO PENDENTES`,
            name: '', inci: '', category: '', needed: '',
            unit: '', missing: '', unitPrice: '', totalPriceMissing: '',
            totalPriceTotal: '', supplier: '', type: 'TITLE',
        });
        titleRow.font = { bold: true, size: 14, color: { argb: 'FFFFFFFF' } };
        titleRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1A3A5C' } };
        titleRow.alignment = { horizontal: 'left', vertical: 'middle' };
        titleRow.height = 30;
        worksheet.mergeCells(`A${titleRow.number}:L${titleRow.number}`);

        // OPs list
        const opsListRow = worksheet.addRow({
            code: `OPs incluídas: ${pendingOps.map(op => op.opCode).join(', ')}`,
            name: '', inci: '', category: '', needed: '',
            unit: '', missing: '', unitPrice: '', totalPriceMissing: '',
            totalPriceTotal: '', supplier: '', type: 'OPS_LIST',
        });
        opsListRow.font = { italic: true, color: { argb: 'FF666666' } };
        opsListRow.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
        opsListRow.height = 40;
        worksheet.mergeCells(`A${opsListRow.number}:L${opsListRow.number}`);

        worksheet.addRow({
            code: '', name: '', inci: '', category: '', needed: '',
            unit: '', missing: '', unitPrice: '', totalPriceMissing: '',
            totalPriceTotal: '', supplier: '', type: 'EMPTY'
        });

        // Categorize and add data
        const unitCategoryIds = allCategories
            .filter(c => ['SERIGRAFIA', 'EMBALAGEM'].includes(c.name?.toUpperCase()))
            .map(c => c.id);

        const mpItems = [];
        const embItems = [];

        Object.values(allTotals).forEach(t => {
            const rmData = allRawMaterials.find(rm => rm.id === t.id);
            const isUnit = rmData?.categoryIds?.some(id => unitCategoryIds.includes(id));
            if (isUnit) {
                embItems.push(t);
            } else {
                mpItems.push(t);
            }
        });

        const addDataRows = (items, sectionType) => {
            let sumTotalMissing = 0;
            let sumTotalTotal = 0;

            items.forEach(t => {
                const rmData = allRawMaterials.find(rm => rm.id === t.id);
                const productData = products.find(p => p.id === t.id);
                const dataSource = rmData || productData;

                let categoryNames = [];
                if (rmData?.categoryIds) {
                    categoryNames = rmData.categoryIds
                        .map(catId => {
                            const c = allCategories.find(c => c.id === catId);
                            return c ? c.name : '';
                        })
                        .filter(Boolean);
                } else if (productData?.categoryIds) {
                    categoryNames = productData.categoryIds
                        .map(catId => {
                            const c = allCategories.find(c => c.id === catId);
                            return c ? c.name : '';
                        })
                        .filter(Boolean);
                }

                let uPrice = 0;
                let supplierName = 'Nenhum cadastrado';

                if (productData) {
                    uPrice = parseFloat(productData.totalFormulationCost) || 0;
                    supplierName = 'Produção Interna (Semi Acabado)';
                } else if (rmData?.suppliers?.length > 0) {
                    uPrice = parseFloat(rmData.suppliers[0].valor) || 0;
                    supplierName = rmData.suppliers[0].razaoSocial || 'N/A';
                }

                const stockActual = t.isWater ? t.needed : (t.stock || 0);
                const difference = Math.max(0, t.needed - stockActual);
                const totalMissing = difference * uPrice;
                const totalTotal = t.needed * uPrice;

                sumTotalMissing += totalMissing;
                sumTotalTotal += totalTotal;

                worksheet.addRow({
                    code: dataSource?.code || '—',
                    name: dataSource?.commercialName || '—',
                    inci: rmData?.inciName || productData?.commercialName || '—',
                    category: categoryNames.join(', ') || 'Sem categoria',
                    needed: Number(t.needed.toFixed(3)),
                    unit: t.unit,
                    missing: Number(difference.toFixed(3)),
                    unitPrice: uPrice,
                    totalPriceMissing: Number(totalMissing.toFixed(2)),
                    totalPriceTotal: Number(totalTotal.toFixed(2)),
                    supplier: supplierName,
                    type: sectionType,
                });
            });

            return { sumTotalMissing, sumTotalTotal };
        };

        // Add MP section
        if (mpItems.length > 0) {
            const headerRow = worksheet.addRow({
                code: 'MATÉRIAS-PRIMAS', name: '', inci: '', category: '', needed: '',
                unit: '', missing: '', unitPrice: '', totalPriceMissing: '',
                totalPriceTotal: '', supplier: '', type: 'MP_HEADER',
            });
            headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2E4057' } };
            headerRow.alignment = { horizontal: 'left', vertical: 'middle' };
            headerRow.height = 25;
            worksheet.mergeCells(`A${headerRow.number}:L${headerRow.number}`);

            const mpTotals = addDataRows(mpItems, 'Matéria-Prima');

            if (mpItems.length > 1) {
                const subtotalRow = worksheet.addRow({
                    code: '', name: '', inci: '', category: '', needed: '',
                    unit: '', missing: '', unitPrice: 'SUBTOTAL MP:',
                    totalPriceMissing: Number(mpTotals.sumTotalMissing.toFixed(2)),
                    totalPriceTotal: Number(mpTotals.sumTotalTotal.toFixed(2)),
                    supplier: '', type: 'MP_SUBTOTAL',
                });
                subtotalRow.font = { bold: true, color: { argb: 'FF1A3A5C' } };
                subtotalRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8EDF2' } };
            }
        }

        // Add EMB section
        if (embItems.length > 0) {
            worksheet.addRow({
                code: '', name: '', inci: '', category: '', needed: '',
                unit: '', missing: '', unitPrice: '', totalPriceMissing: '',
                totalPriceTotal: '', supplier: '', type: 'EMPTY'
            });

            const headerRow = worksheet.addRow({
                code: 'EMBALAGENS E SERIGRAFIA', name: '', inci: '', category: '', needed: '',
                unit: '', missing: '', unitPrice: '', totalPriceMissing: '',
                totalPriceTotal: '', supplier: '', type: 'EMB_HEADER',
            });
            headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2E4057' } };
            headerRow.alignment = { horizontal: 'left', vertical: 'middle' };
            headerRow.height = 25;
            worksheet.mergeCells(`A${headerRow.number}:L${headerRow.number}`);

            const embTotals = addDataRows(embItems, 'Embalagem/Serigrafia');

            if (embItems.length > 1) {
                const subtotalRow = worksheet.addRow({
                    code: '', name: '', inci: '', category: '', needed: '',
                    unit: '', missing: '', unitPrice: 'SUBTOTAL EMB:',
                    totalPriceMissing: Number(embTotals.sumTotalMissing.toFixed(2)),
                    totalPriceTotal: Number(embTotals.sumTotalTotal.toFixed(2)),
                    supplier: '', type: 'EMB_SUBTOTAL',
                });
                subtotalRow.font = { bold: true, color: { argb: 'FF1A3A5C' } };
                subtotalRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8EDF2' } };
            }
        }

        // Grand Total
        const allItems = [...mpItems, ...embItems];
        if (allItems.length > 0) {
            let grandTotalMissing = 0;
            let grandTotalTotal = 0;

            allItems.forEach(t => {
                const rmData = allRawMaterials.find(rm => rm.id === t.id);
                const productData = products.find(p => p.id === t.id);
                let uPrice = 0;
                if (productData) {
                    uPrice = parseFloat(productData.totalFormulationCost) || 0;
                } else if (rmData?.suppliers?.length > 0) {
                    uPrice = parseFloat(rmData.suppliers[0].valor) || 0;
                }
                const stockActual = t.isWater ? t.needed : (t.stock || 0);
                const difference = Math.max(0, t.needed - stockActual);
                grandTotalMissing += difference * uPrice;
                grandTotalTotal += t.needed * uPrice;
            });

            worksheet.addRow({
                code: '', name: '', inci: '', category: '', needed: '',
                unit: '', missing: '', unitPrice: '', totalPriceMissing: '',
                totalPriceTotal: '', supplier: '', type: 'EMPTY'
            });

            const totalRow = worksheet.addRow({
                code: '', name: '', inci: '', category: '', needed: '',
                unit: '', missing: '', unitPrice: 'TOTAL GERAL:',
                totalPriceMissing: Number(grandTotalMissing.toFixed(2)),
                totalPriceTotal: Number(grandTotalTotal.toFixed(2)),
                supplier: '', type: 'GRAND_TOTAL',
            });
            totalRow.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
            totalRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1A3A5C' } };
            totalRow.height = 30;
        }

        // Formatting
        const currencyFormat = '"R$ " #,##0.00';

        worksheet.eachRow((row, rowNumber) => {
            const cellValue = row.getCell(1).value;
            const typeValue = row.getCell('type')?.value;
            const isSectionHeader = cellValue === 'MATÉRIAS-PRIMAS' || cellValue === 'EMBALAGENS E SERIGRAFIA';
            const isSpecialRow = isSectionHeader ||
                typeValue === 'MP_SUBTOTAL' || typeValue === 'EMB_SUBTOTAL' ||
                typeValue === 'GRAND_TOTAL' || typeValue === 'EMPTY' ||
                typeValue === 'TITLE' || typeValue === 'OPS_LIST';

            if (!isSpecialRow) {
                row.eachCell({ includeEmpty: true }, (cell) => {
                    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
                    cell.border = {
                        top: { style: 'thin' }, left: { style: 'thin' },
                        bottom: { style: 'thin' }, right: { style: 'thin' }
                    };
                });

                if (rowNumber === 1) {
                    row.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                    row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1A3A5C' } };
                } else {
                    if (row.getCell('unitPrice').value && typeof row.getCell('unitPrice').value === 'number') {
                        row.getCell('unitPrice').numFmt = currencyFormat;
                        row.getCell('totalPriceMissing').numFmt = currencyFormat;
                        row.getCell('totalPriceTotal').numFmt = currencyFormat;
                    }
                    if (rowNumber % 2 === 0) {
                        row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } };
                    }
                }
            }
        });

        const lastRow = worksheet.rowCount;
        const lastCol = worksheet.columnCount;
        worksheet.autoFilter = {
            from: { row: 1, column: 1 },
            to: { row: lastRow, column: lastCol - 1 }
        };

        const typeColumn = worksheet.getColumn('type');
        if (typeColumn) typeColumn.hidden = true;

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `OPs_Pendentes_Consolidado_${new Date().getTime()}.xlsx`);

        showNotification(`${pendingOps.length} OPs pendentes exportadas com sucesso!`, 'success');

    } catch (err) {
        console.error("Erro ao exportar OPs pendentes:", err);
        showNotification("Erro ao exportar. Verifique o console.", 'error');
    }
};